import React, { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { 
  ChevronRight, 
  AlertCircle,
  Droplets,
  Lightbulb,
  Settings,
  CheckCircle2,
  ArrowLeft,
  Phone,
  Mail,
  BookOpen,
  ChevronLeft,
  ChevronRight as ChevronRightIcon
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import TopBar from "../components/TopBar";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { buildBreadcrumb, buildFaq, orgReference, SITE_URL } from "../lib/seoData";
import { getDefectBySlug, getAllDefects } from "../data/troubleshootingData";

const DefectPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const defect = getDefectBySlug(slug);
  const allDefects = getAllDefects();

  // Find previous and next defects for navigation
  const currentIndex = allDefects.findIndex(d => d.slug === slug);
  const prevDefect = currentIndex > 0 ? allDefects[currentIndex - 1] : null;
  const nextDefect = currentIndex < allDefects.length - 1 ? allDefects[currentIndex + 1] : null;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!defect) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Defect Not Found</h1>
          <Link to="/gravure-troubleshooting" className="text-[#8B1538] hover:underline">
            Return to Troubleshooting Guide
          </Link>
        </div>
      </div>
    );
  }

  const BulletList = ({ items, icon: Icon, iconColor = "text-[#8B1538]" }) => (
    <ul className="space-y-3">
      {items.map((item, index) => (
        <li key={index} className="flex items-start gap-3">
          <Icon className={`h-5 w-5 ${iconColor} flex-shrink-0 mt-0.5`} />
          <span className="text-gray-700">{item}</span>
        </li>
      ))}
    </ul>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title={defect.metaTitle}
        description={defect.metaDescription}
        path={`/gravure-troubleshooting/${defect.slug}`}
        keywords={`${defect.defectName}, gravure printing ${defect.defectName.toLowerCase()}, gravure printing defect, ${defect.defectName.toLowerCase()} causes and solutions`}
        jsonLd={[
          buildBreadcrumb([
            { name: "Home", path: "/" },
            { name: "Gravure Troubleshooting Guide", path: "/gravure-troubleshooting" },
            { name: defect.defectName, path: `/gravure-troubleshooting/${defect.slug}` },
          ]),
          {
            "@type": "TechArticle",
            headline: `${defect.defectName} in Gravure Printing – Causes & Solutions`,
            description: defect.metaDescription,
            url: `${SITE_URL}/gravure-troubleshooting/${defect.slug}`,
            author: orgReference,
            publisher: orgReference,
            articleSection: "Gravure Printing Troubleshooting",
          },
          buildFaq([
            {
              question: `What causes ${defect.defectName} in gravure printing?`,
              answer: [...defect.probableCauses, ...defect.inkRelatedCauses].join(". ") + ".",
            },
            {
              question: `How do you fix ${defect.defectName} in gravure printing?`,
              answer: defect.recommendedSolutions.join(". ") + ".",
            },
            {
              question: `What are the common symptoms of ${defect.defectName}?`,
              answer: defect.commonSymptoms.join(". ") + ".",
            },
          ]),
        ]}
      />
      <TopBar />
      
      {/* Header */}
      <header className="fixed top-10 left-0 right-0 z-40 bg-white shadow-lg py-3">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3">
              <img
                src="/crimson-logo-with-name.png"
                alt="Crimson Inks"
                className="h-14 w-auto object-contain"
              />
            </Link>
            <nav className="hidden lg:flex items-center space-x-2 text-sm">
              <Link to="/" className="text-gray-500 hover:text-[#8B1538] transition-colors">
                Home
              </Link>
              <ChevronRight className="h-4 w-4 text-gray-400" />
              <Link to="/gravure-troubleshooting" className="text-gray-500 hover:text-[#8B1538] transition-colors">
                Technical Support
              </Link>
              <ChevronRight className="h-4 w-4 text-gray-400" />
              <span className="text-[#8B1538] font-medium truncate max-w-[200px]">
                {defect.defectName}
              </span>
            </nav>
            <Link to="/#contact">
              <Button className="bg-[#8B1538] hover:bg-[#DC143C] text-white px-6 py-2 rounded-md font-semibold hidden sm:flex">
                Contact Us
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Back Button */}
      <div className="pt-28 pb-4 bg-white border-b">
        <div className="container mx-auto px-4 lg:px-8">
          <Link 
            to="/gravure-troubleshooting"
            className="inline-flex items-center text-gray-600 hover:text-[#8B1538] transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Troubleshooting Guide
          </Link>
        </div>
      </div>

      {/* Hero Section */}
      <section className="py-12 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-10 left-10 w-40 h-40 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-60 h-60 bg-[#8B1538] rounded-full blur-3xl"></div>
        </div>
        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <div className="max-w-4xl">
            <div className="inline-flex items-center bg-[#8B1538] px-4 py-2 rounded-full mb-6">
              <BookOpen className="h-4 w-4 mr-2" />
              <span className="text-sm font-medium">Gravure Printing Defect</span>
            </div>
            <h1 className="text-3xl lg:text-4xl font-bold mb-6" data-testid="defect-title">
              {defect.defectName} in Gravure Printing
            </h1>
            <p className="text-lg text-gray-300 leading-relaxed max-w-3xl">
              {defect.introDescription}
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto space-y-8">
            
            {/* Common Symptoms */}
            <Card className="border-2 border-gray-100 shadow-lg">
              <CardHeader className="bg-red-50 border-b">
                <CardTitle className="flex items-center text-xl text-gray-900">
                  <AlertCircle className="h-6 w-6 text-red-600 mr-3" />
                  Common Symptoms
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <BulletList 
                  items={defect.commonSymptoms} 
                  icon={AlertCircle}
                  iconColor="text-red-500"
                />
              </CardContent>
            </Card>

            {/* Probable Causes */}
            <Card className="border-2 border-gray-100 shadow-lg">
              <CardHeader className="bg-amber-50 border-b">
                <CardTitle className="flex items-center text-xl text-gray-900">
                  <Settings className="h-6 w-6 text-amber-600 mr-3" />
                  Probable Causes
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <BulletList 
                  items={defect.probableCauses} 
                  icon={Settings}
                  iconColor="text-amber-600"
                />
              </CardContent>
            </Card>

            {/* Ink-Related Causes */}
            <Card className="border-2 border-gray-100 shadow-lg">
              <CardHeader className="bg-blue-50 border-b">
                <CardTitle className="flex items-center text-xl text-gray-900">
                  <Droplets className="h-6 w-6 text-blue-600 mr-3" />
                  Ink-Related Causes
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <BulletList 
                  items={defect.inkRelatedCauses} 
                  icon={Droplets}
                  iconColor="text-blue-600"
                />
              </CardContent>
            </Card>

            {/* Recommended Solutions */}
            <Card className="border-2 border-gray-100 shadow-lg">
              <CardHeader className="bg-green-50 border-b">
                <CardTitle className="flex items-center text-xl text-gray-900">
                  <Lightbulb className="h-6 w-6 text-green-600 mr-3" />
                  Recommended Solutions
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <BulletList 
                  items={defect.recommendedSolutions} 
                  icon={CheckCircle2}
                  iconColor="text-green-600"
                />
              </CardContent>
            </Card>

            {/* Press-Side Best Practices */}
            <Card className="border-2 border-gray-100 shadow-lg">
              <CardHeader className="bg-purple-50 border-b">
                <CardTitle className="flex items-center text-xl text-gray-900">
                  <Settings className="h-6 w-6 text-purple-600 mr-3" />
                  Press-Side Best Practices
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <BulletList 
                  items={defect.pressSideBestPractices} 
                  icon={CheckCircle2}
                  iconColor="text-purple-600"
                />
              </CardContent>
            </Card>

            {/* Crimson Inks Recommendation */}
            <Card className="border-2 border-[#8B1538] shadow-xl bg-gradient-to-br from-[#8B1538]/5 to-white">
              <CardHeader className="bg-[#8B1538] text-white">
                <CardTitle className="flex items-center text-xl">
                  <img 
                    src="/crimson-logo.png" 
                    alt="Crimson Inks"
                    className="h-8 w-8 mr-3 bg-white rounded-full p-1"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                  Crimson Inks Recommendation
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <p className="text-gray-800 text-lg leading-relaxed">
                  {defect.crimsonRecommendation}
                </p>
              </CardContent>
            </Card>

          </div>
        </div>
      </section>

      {/* Navigation Between Defects */}
      <section className="py-8 bg-gray-100 border-t">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            {prevDefect ? (
              <Link 
                to={`/gravure-troubleshooting/${prevDefect.slug}`}
                className="flex items-center text-gray-600 hover:text-[#8B1538] transition-colors group"
              >
                <ChevronLeft className="h-5 w-5 mr-2 group-hover:-translate-x-1 transition-transform" />
                <div className="text-left">
                  <span className="text-xs text-gray-400 block">Previous</span>
                  <span className="font-medium">{prevDefect.defectName}</span>
                </div>
              </Link>
            ) : <div />}
            
            {nextDefect ? (
              <Link 
                to={`/gravure-troubleshooting/${nextDefect.slug}`}
                className="flex items-center text-gray-600 hover:text-[#8B1538] transition-colors group"
              >
                <div className="text-right">
                  <span className="text-xs text-gray-400 block">Next</span>
                  <span className="font-medium">{nextDefect.defectName}</span>
                </div>
                <ChevronRightIcon className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            ) : <div />}
          </div>
        </div>
      </section>

      {/* Sticky CTA */}
      <section className="py-12 bg-gradient-to-r from-[#8B1538] to-[#DC143C] sticky bottom-0 z-30">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-white text-center md:text-left">
              <h3 className="text-xl font-bold">Need Technical Assistance?</h3>
              <p className="text-white/80">Contact Crimson Inks for expert support</p>
            </div>
            <div className="flex items-center gap-4">
              <a 
                href="tel:+917706994400"
                className="inline-flex items-center bg-white text-[#8B1538] px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
              >
                <Phone className="h-5 w-5 mr-2" />
                Call Now
              </a>
              <a 
                href="mailto:Crimson.inks7475@gmail.com"
                className="inline-flex items-center bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 px-6 py-3 rounded-lg font-semibold hover:bg-white/20 transition-colors"
              >
                <Mail className="h-5 w-5 mr-2" />
                Email Us
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DefectPage;
